import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PostsDataService, PostsColumnsService } from '../../services';
import { DialogPostFormComponent } from './post-form/post-form.component';
import { DialogPostDetailsComponent } from './post-details/post-details.component';
import { Post, PostsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-posts',
  templateUrl: './posts.component.html',
  styleUrls: ['./posts.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PostsComponent implements OnInit, OnDestroy {

  private postAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private postEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private postDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public postToDelete: Post = {};
  public posts: Post[] = [];
  public post: Post = {};
  public selectedPosts: Post[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private postsDataService: PostsDataService,
    private postsColumnsService: PostsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PostsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Posts - Management');
    this.primengConfig.ripple = true;
    this.getPosts();
  }

  ngOnDestroy(): void {
    console.log('PostsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPosts(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.postsDataService.getPosts(this.parameters).subscribe((response: PostsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.posts = response.results;
        } else {
          this.posts = this.posts.concat(response.results);
        }
        if (response.inlineCount > this.posts.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPosts();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTLOADED')});
    });
  }

  importPosts(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.postsDataService.addPosts(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTSCREATED')});
                if (proceed === j) {
                  this.getPosts();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.postsDataService.updatePosts(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTSUPDATED')});
                if (proceed === j) {
                  this.getPosts();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.postsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('posts.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPosts() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Posts?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.postsDataService.deletePosts(this.selectedPosts).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Posts Deleted', life: 3000});
            this.getPosts();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Posts not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Posts not deleted'});
        });
        this.selectedPosts = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.posts, this.columns, 'Posts');
  }

  exportExcel() {
    ToolsService.exportExcel(this.posts, this.columns, 'Posts');
  }

  exportCsv() {
    ToolsService.exportCsv(this.posts, this.columns, 'Posts');
  }

  refresh() {
    this.postsDataService.clearCache();
    this.getPosts();
  }

  postAdd(): void {
    if (!this.navigation.getParamValue('posts.addInDialog', true)) {
      this.router.navigate(['/posts/add']);
      return;
    }
    this.postAddDialog = this.dialogService.open(DialogPostFormComponent, {
      header: '', // this.translate.instant('NEWPOST'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        post: {id: null},
        postTypes: this.postsColumnsService.postTypes,
        users: this.postsColumnsService.users,
      },
    });

    this.postAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Post added'});
        this.getPosts();
      }
    });
  }

  postEdit(post: Post): void {
    if (!this.navigation.getParamValue('posts.editInDialog', true)) {
      this.router.navigate(['/posts/edit/' + post.id]);
      return;
    }
    this.postEditDialog = this.dialogService.open(DialogPostFormComponent, {
      header: '', // this.translate.instant('EDITPOST') + ' ' + post.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        post: post,
        postTypes: this.postsColumnsService.postTypes,
        users: this.postsColumnsService.users,
      },
    });

    this.postEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Post updated'});
        this.getPosts();
      }
    });
  }

  postDetails(post: Post): void {
    if (!this.navigation.getParamValue('posts.detailsInDialog', true)) {
      this.router.navigate(['/posts/details/' + post.id]);
      return;
    }
    this.postDetailsDialog = this.dialogService.open(DialogPostDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        post: post,
      }
    });
  }

  postDelete(post: Post): void {
    this.postToDelete = post;
    this.messageService.clear();
    this.messageService.add({
      key: 'postDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + post.title + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.postsDataService.deletePost(this.postToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Post deleted'});
      this.getPosts();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Post not deleted'});
    });
  }

  onDeleteReject(): void {
    this.postToDelete = {};
    this.messageService.clear('postDelete');
  }

}
